/* eslint-disable @typescript-eslint/restrict-template-expressions */
import Form from 'components/Form';
import { useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useQuery } from 'v3/hooks/useQuery';
import { useConnectBankAccount } from 'hooks/useConnectBankAccount';
import { useMixpanel } from 'v3/hooks/useMixpanel';
import { PaymentClient } from 'api/clients/PaymentClient';

import { ModalNotification } from 'components/ModalNotification/ModalNotification';
import Header from 'components/Header';
import { Col, Container, Row } from 'components/GridSystem';
import Footer from 'components/Footer';
import CheckboxField from 'components/CheckboxField';
import FloatPlaidButton from 'components/FloatPlaidButton';
import PlaidButton from 'components/PlaidButton';
import LoadingSpinner from 'components/LoadingSpinner';

import * as S from './styles';
import plaidLogo from 'assets/images/plaid-logo.png';
import { useDispatch } from 'react-redux';
import { setLeadData } from 'leadData/leadSlice';

const ConnectBankAccountInfoSchema = Yup.object().shape({
  agreeTerms: Yup.bool()
    .required('You must agree with to ACH Authorization Agreement listed above')
    .oneOf(
      [true],
      'You must agree with to ACH Authorization Agreement listed above'
    ),
});

const ConnectBankAccount = () => {
  const query = useQuery();
  const userUuid = query.get('userUuid') ?? '';
  const cfUuid = query.get('cfUuid') ?? '';
  const clubId = query.get('clubId') ? Number(query.get('clubId')) : 0;
  const name = query.get('name');
  const email = query.get('email')?.replace(' ', '+');

  const [plaidInfo, setPlaidInfo] = useState<any>({
    bankAccountManuallyVerificationPending: false,
    bankAccountVerified: false,
    clubId,
    debit: 0,
    dueDate: '',
    plaidAccountId: '',
    plaidPublicToken: '',
    userUuid: '',
  });

  const { open, modal, setModal, isSubmitting } = useConnectBankAccount({
    userUuid,
    clubId,
    cfUuid,
  });

  const mp = useMixpanel();
  const dispatch = useDispatch();

  useEffect(() => {
    const load = async () => {
      const [data] = await PaymentClient().paymentInfo(cfUuid);

      if (data) {
        setPlaidInfo(data);
      }
    };

    void load();
  }, []);

  useEffect(() => {
    if (email) {
      dispatch(setLeadData({ leadData: { email } }));
    }
  }, [email, dispatch]);

  return (
    <S.Wrapper>
      <ModalNotification
        isOpen={modal.isOpen}
        close={() => {
          setModal({ ...modal, isOpen: false });
        }}
        {...modal.payload}
      />
      <Formik
        initialValues={{
          agreeTerms: false,
        }}
        validationSchema={ConnectBankAccountInfoSchema}
        onSubmit={() => console.log('')}
      >
        {(formik) => {
          const { errors, touched, values } = formik;

          return (
            <>
              <Header variant="primary" />

              <S.Main>
                <Container>
                  <Row>
                    <Col xs={12} sm={12} md={12} lg={12} xl={12}>
                      <S.TitleSection>
                        <S.UserTitle>
                          Hi, {name}! <span>❤️</span>
                        </S.UserTitle>

                        {plaidInfo.bankAccountManuallyVerificationPending ? (
                          <S.Description>Check your account</S.Description>
                        ) : (
                          <S.Description>
                            We ask that you provide us with your bank account
                            information to debit your first payment via Plaid
                            Link.
                          </S.Description>
                        )}
                      </S.TitleSection>
                    </Col>
                  </Row>
                </Container>

                <S.WhiteSection>
                  <Container>
                    <Row>
                      <Col xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Form title="Contract Agreement" bgcolor="white">
                          <S.Title data-cy="payment-info-title">
                            Payment Information
                          </S.Title>

                          {plaidInfo.bankAccountManuallyVerificationPending ? (
                            <>
                              <S.Text>
                                Please put your credentials again and deposits
                                in order. Savings Club validates each bank
                                account entered using Plaid and we use the
                                Stripe for the manegement payments.
                              </S.Text>

                              <S.Text>
                                <a
                                  href="https://plaid.com/"
                                  onClick={mp.plaidHelpClick}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Learn more about Plaid.
                                </a>
                              </S.Text>

                              <PlaidButton
                                icon={
                                  !isSubmitting ? (
                                    <img src={plaidLogo} alt="Plaid" />
                                  ) : undefined
                                }
                                onClick={() => {
                                  open();
                                  mp.registerPlaidClick();
                                }}
                                type="button"
                              >
                                {isSubmitting ? <LoadingSpinner /> : 'connect'}
                              </PlaidButton>
                            </>
                          ) : (
                            <>
                              <S.Text>
                                We are asking you to provide us your bank
                                account information in this form to debit the
                                first payment from your bank account through
                                Plaid Link a secure way to provide your bank
                                details and verify your account.
                              </S.Text>

                              <S.Text>
                                Please put your credentials again and deposits
                                in order. Savings Club validates each bank
                                account entered using Plaid and we use the
                                Stripe for the manegement payments.
                              </S.Text>

                              <S.Text>
                                <a
                                  href="https://plaid.com/"
                                  onClick={mp.plaidHelpClick}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Learn more about Plaid.
                                </a>
                              </S.Text>

                              <S.SubTitle>
                                ACH Authorization Agreement
                              </S.SubTitle>

                              <S.Text>
                                I hereby authorize and request Savings Club of
                                Texas LLC to debit funds from my account at the
                                Financial Institution indicated, and credit the
                                funds. I authorize HTHB Technologies LLC to
                                take any and all action required to correct any
                                errors.
                              </S.Text>

                              <S.Text>
                                By clicking the button below, you certify that
                                the information you have given on this ACH Debit
                                Authorization Agreement for Direct Payments is
                                complete, true, and submitted for the purpose
                                selected above. It will remain in effect until |
                                notify HTHB Technologies LLC that | wish to.
                              </S.Text>

                              <CheckboxField
                                name="agreeTerms"
                                required
                                feedbackMessage={
                                  touched.agreeTerms && errors.agreeTerms
                                }
                                isValid={
                                  !(touched.agreeTerms && errors.agreeTerms)
                                }
                              >
                                <span>
                                  I have read, understand and agree to{' '}
                                  <strong>ACH Authorization Agreement</strong>{' '}
                                  listed above
                                </span>
                              </CheckboxField>

                              <FloatPlaidButton
                                icon={
                                  !isSubmitting ? (
                                    <img src={plaidLogo} alt="Plaid" />
                                  ) : undefined
                                }
                                onClick={() => {
                                  open();
                                  mp.registerPlaidClick();
                                }}
                                disabled={!values.agreeTerms}
                                type="submit"
                              >
                                {isSubmitting ? <LoadingSpinner /> : 'pay with'}
                              </FloatPlaidButton>
                            </>
                          )}
                        </Form>
                      </Col>
                    </Row>
                  </Container>
                </S.WhiteSection>
              </S.Main>

              <Footer />
            </>
          );
        }}
      </Formik>
    </S.Wrapper>
  );
};

export default ConnectBankAccount;
